import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { authenticatedFetch, getApiUrl } from '@/utils/auth/authService';
import { log } from '@/utils/core/vueLogger.js';
import { CacheManager } from '@/utils/core/cacheManager.js';

const deliveryLocationCache = new CacheManager('VueCache', 'cache', {
    maxAge: 24 * 60 * 60 * 1000,
    memoryCacheTTL: 30 * 60 * 1000,
    logCategory: 'DeliveryLocation',
    keyPrefix: () => `${iframeDataBridge.getCustomer() ?? 'unknown'}.${iframeDataBridge.getCurrentUser()?.id ?? 'unknown'}.deliveryLocations.`,
});

export async function getDeliveryLocations(forceRefresh = false) {
    if (!forceRefresh && !deliveryLocationCache.getFromMemory('deliveryLocations')) {
        try {
            const legacyData = await iframeDataBridge.getStorageAsync('DELIVERYLOCATIONS');
            if (legacyData) {
                log('info', 'DeliveryLocation', '📦 Seeding Vue cache from legacy storage (DELIVERYLOCATIONS)');
                await deliveryLocationCache.put('deliveryLocations', legacyData);
                deliveryLocationCache.setInMemory('deliveryLocations', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'DeliveryLocation', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    return deliveryLocationCache.fetchOrCache('deliveryLocations', fetchDeliveryLocationsFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

export async function setLegacyDeliveryLocations(deliveryLocations){
    const win = iframeDataBridge.getIframeWindow();
    if (win?.externis?.ecosVM) {
        win.externis.ecosVM.deliveryLocations = deliveryLocations;
    }
    iframeDataBridge.invalidateCachedEcosVM();
}

export async function getDeliveryLocationTypes(forceRefresh = false) {
    if (!forceRefresh && !deliveryLocationCache.getFromMemory('deliveryLocationTypes')) {
        try {
            const legacyData = await iframeDataBridge.getStorageAsync('DLT');
            if (legacyData) {
                log('info', 'DeliveryLocation', '📦 Seeding Vue cache from legacy storage (DLT)');
                await deliveryLocationCache.put('deliveryLocationTypes', legacyData);
                deliveryLocationCache.setInMemory('deliveryLocationTypes', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'DeliveryLocation', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    return deliveryLocationCache.fetchOrCache('deliveryLocationTypes', fetchDeliveryLocationTypesFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

export async function getDeliveryLocationDelays(forceRefresh = false) {
    if (!forceRefresh && !deliveryLocationCache.getFromMemory('deliveryLocationDelays')) {
        try {
            const legacyData = await iframeDataBridge.getStorageAsync('DELIVERYLOCATIONDELAYS');
            if (legacyData) {
                log('info', 'DeliveryLocation', '📦 Seeding Vue cache from legacy storage (DELIVERYLOCATIONDELAYS)');
                await deliveryLocationCache.put('deliveryLocationDelays', legacyData);
                deliveryLocationCache.setInMemory('deliveryLocationDelays', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'DeliveryLocation', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    return deliveryLocationCache.fetchOrCache('deliveryLocationDelays', fetchDeliveryLocationDelaysFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

export async function getDeliveryLocationDocuments(forceRefresh = false) {
    if (!forceRefresh && !deliveryLocationCache.getFromMemory('deliveryLocationDocuments')) {
        try {
            const legacyData = await iframeDataBridge.getStorageAsync('DELIVERYLOCATIONDOCUMENTS');
            if (legacyData) {
                log('info', 'DeliveryLocation', '📦 Seeding Vue cache from legacy storage (DELIVERYLOCATIONDOCUMENTS)');
                await deliveryLocationCache.put('deliveryLocationDocuments', legacyData);
                deliveryLocationCache.setInMemory('deliveryLocationDocuments', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'DeliveryLocation', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    return deliveryLocationCache.fetchOrCache('deliveryLocationDocuments', fetchDeliveryLocationDocumentsFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

export async function getDeliveryLocationsGeo(forceRefresh = false) {
    if (!forceRefresh && !deliveryLocationCache.getFromMemory('deliveryLocationsGeo')) {
        try {
            const legacyData = await iframeDataBridge.getStorageAsync('DELIVERYLOCATIONGEO');
            if (legacyData) {
                log('info', 'DeliveryLocation', '📦 Seeding Vue cache from legacy storage (DELIVERYLOCATIONGEO)');
                await deliveryLocationCache.put('deliveryLocationsGeo', legacyData);
                deliveryLocationCache.setInMemory('deliveryLocationsGeo', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'DeliveryLocation', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    return deliveryLocationCache.fetchOrCache('deliveryLocationsGeo', fetchDeliveryLocationsGeoFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

export async function getDeliveryLocationFurnishings(forceRefresh = false) {
    if (!forceRefresh && !deliveryLocationCache.getFromMemory('deliveryLocationFurnishings')) {
        try {
            const legacyData = await iframeDataBridge.getStorageAsync('SFA_FURNISHING');
            if (legacyData) {
                log('info', 'DeliveryLocation', '📦 Seeding Vue cache from legacy storage (SFA_FURNISHING)');
                await deliveryLocationCache.put('deliveryLocationFurnishings', legacyData);
                deliveryLocationCache.setInMemory('deliveryLocationFurnishings', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'DeliveryLocation', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    return deliveryLocationCache.fetchOrCache('deliveryLocationFurnishings', fetchDeliveryLocationFurnishingsFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

export async function getDeliveryLocationContacts(deliveryLocationId) {
    try {
        if (!iframeDataBridge.isOnline()) {
            log('warn', 'DeliveryLocation', '📴 Offline: Cannot fetch delivery location contacts');
            return [];
        }

        const response = await authenticatedFetch(getApiUrl('Sales/DeliveryLocationContacts'), { method: 'GET' }, 'tokenSales');

        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

        const contacts = await response.json();

        log('success', 'DeliveryLocation', `✅ Fetched ${contacts.length} delivery location contacts`);
        return contacts;
    } catch (error) {
        log('error', 'DeliveryLocation', `❌ Failed to fetch delivery location contacts from API: ${error.message}`);
        return [];
    }
}

export async function getDeliveryLocationRestrictions(forceRefresh = false) {
    if (!forceRefresh && !deliveryLocationCache.getFromMemory('deliveryLocationRestrictions')) {
        try {
            const legacyData = await iframeDataBridge.getStorageAsync('DELIVERYLOCATIONRESTRICTIONS');
            if (legacyData) {
                log('info', 'DeliveryLocation', '📦 Seeding Vue cache from legacy storage (DELIVERYLOCATIONRESTRICTIONS)');
                await deliveryLocationCache.put('deliveryLocationRestrictions', legacyData);
                deliveryLocationCache.setInMemory('deliveryLocationRestrictions', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'DeliveryLocation', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    return deliveryLocationCache.fetchOrCache('deliveryLocationRestrictions', fetchDeliveryLocationRestrictionsFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

export async function getDeliveryLocationSegmentValidations(forceRefresh = false) {
    if (!forceRefresh && !deliveryLocationCache.getFromMemory('deliveryLocationSegmentValidations')) {
        try {
            const legacyData = await iframeDataBridge.getStorageAsync('DELIVERYLOCATIONSEGMENTVALIDATIONS');
            if (legacyData) {
                log('info', 'DeliveryLocation', '📦 Seeding Vue cache from legacy storage (DELIVERYLOCATIONSEGMENTVALIDATIONS)');
                await deliveryLocationCache.put('deliveryLocationSegmentValidations', legacyData);
                deliveryLocationCache.setInMemory('deliveryLocationSegmentValidations', legacyData);
                return legacyData;
            }
        } catch (legacyError) {
            log('warn', 'DeliveryLocation', `⚠️ Legacy storage check failed: ${legacyError.message}`);
        }
    }

    return deliveryLocationCache.fetchOrCache('deliveryLocationSegmentValidations', fetchDeliveryLocationSegmentValidationsFromAPI, { forceRefresh, fallback: [], backgroundRefresh: true });
}

async function fetchDeliveryLocationsFromAPI() {
    const response = await authenticatedFetch(getApiUrl('Sales/DeliveryLocations'), { method: 'GET' }, 'tokenSales');
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const deliveryLocations = await response.json();
    log('success', 'DeliveryLocation', `✅ Fetched ${deliveryLocations.length} delivery locations`);
    return deliveryLocations;
}

async function fetchDeliveryLocationTypesFromAPI() {
    const response = await authenticatedFetch(getApiUrl('Sales/DeliveryLocationTypes'), { method: 'GET' }, 'tokenSales');
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const deliveryLocationTypes = await response.json();
    log('success', 'DeliveryLocation', `✅ Fetched ${deliveryLocationTypes.length} delivery location types`);
    return deliveryLocationTypes;
}

async function fetchDeliveryLocationDelaysFromAPI() {
    const response = await authenticatedFetch(getApiUrl('Sales/DeliveryLocationDelays'), { method: 'GET' }, 'tokenSales');
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const deliveryLocationDelays = await response.json();
    log('success', 'DeliveryLocation', `✅ Fetched ${deliveryLocationDelays.length} delivery location delays`);
    return deliveryLocationDelays;
}

async function fetchDeliveryLocationDocumentsFromAPI() {
    const response = await authenticatedFetch(getApiUrl('Sales/DeliveryLocationDocuments'), { method: 'GET' }, 'tokenSales');
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const deliveryLocationDocuments = await response.json();
    log('success', 'DeliveryLocation', `✅ Fetched ${deliveryLocationDocuments.length} delivery location documents`);
    return deliveryLocationDocuments;
}

async function fetchDeliveryLocationsGeoFromAPI() {
    const response = await authenticatedFetch(getApiUrl('Sales/DeliveryLocationsGeo'), { method: 'GET' }, 'tokenSales');
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const deliveryLocationsGeo = await response.json();
    log('success', 'DeliveryLocation', `✅ Fetched ${deliveryLocationsGeo.length} delivery locations geo`);
    return deliveryLocationsGeo;
}

async function fetchDeliveryLocationFurnishingsFromAPI() {
    const response = await authenticatedFetch(getApiUrl('Sales/DeliveryLocationFurnishings'), { method: 'GET' }, 'tokenSales');
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const deliveryLocationFurnishings = await response.json();
    log('success', 'DeliveryLocation', `✅ Fetched ${deliveryLocationFurnishings.length} delivery location furnishings`);
    return deliveryLocationFurnishings;
}

async function fetchDeliveryLocationRestrictionsFromAPI() {
    const response = await authenticatedFetch(getApiUrl('Sales/DeliveryLocationRestrictions'), { method: 'GET' }, 'tokenSales');
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const deliveryLocationRestrictions = await response.json();
    log('success', 'DeliveryLocation', `✅ Fetched ${deliveryLocationRestrictions.length} delivery location restrictions`);
    return deliveryLocationRestrictions;
}

async function fetchDeliveryLocationSegmentValidationsFromAPI() {
    const response = await authenticatedFetch(getApiUrl('Sales/DeliveryLocationSegmentValidations'), { method: 'GET' }, 'tokenSales');
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

    const deliveryLocationSegmentValidations = await response.json();
    log('success', 'DeliveryLocation', `✅ Fetched ${deliveryLocationSegmentValidations.length} delivery location segment validations`);
    return deliveryLocationSegmentValidations;
}

export async function refreshDeliveryLocations() {
    deliveryLocationCache.clearMemory();
    await deliveryLocationCache.clear();
}
